import { useState, useContext, useEffect, useRef } from 'react'
import { t } from '@lingui/core/macro'
import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  Grid,
  Popper,
  Tooltip,
} from '@mui/material'
import { motion } from 'framer-motion'
import { isEmpty, isEqual } from 'lodash'
import { SquareDashedKanban as HeaderIcon } from 'lucide-react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
  Prompt,
  Link,
} from 'react-router-dom'
import { Paper, CvsButton } from '@components'
import { AdminHeader } from '@components/layout/admin-header'
import {
  patternContext,
  listContext,
  fieldSetContext,
  integrationContext,
  usePageTitleContext,
} from '@contexts'
import { api } from 'api'
import { ROUTES, routeByParam } from 'routes'
import { EditTemplateContext } from './edit-template-context'
import { Header } from './header'
import { useSaveTemplate } from './hooks/templateIntegrationSave'
import { useTemplateNavigation } from './hooks/useTemplateNavigation'
import { Integrate } from './integrations/integrate'
import { Preview } from './preview'
import { Schema } from './schema'
import { Setup } from './setup'
import * as css from './styles'
import { setTemplate, readyToValidate, initTemplate, resetStore } from './template.actions'
import { duplicateTemplate } from './utils'
import { validateTemplate } from './validateTemplate'
import { AdminContext } from '../../admin.context'

export const EditTemplate = () => {
  const history = useHistory()
  const location = useLocation()
  const match = useRouteMatch()
  const classes = css.useStyles()
  const requestCache = useRef({})
  const dispatch = useDispatch()
  const { byId: patternsById, fetchPatterns, patternsLoading } = useContext(patternContext)
  const { byId: listsById, fetchLists, listLoading } = useContext(listContext)
  const {
    byId: fieldSetsById,
    fetchAllFieldSetsAndFields,
    fieldsetLoading,
  } = useContext(fieldSetContext)
  const { addPrefix, removePrefix } = usePageTitleContext()
  const { fetchIntegrations, outboundById } = useContext(integrationContext)
  const loading = useSelector(state => state.Template.loading, shallowEqual)
  const anyChange = useSelector(state => state.Template, shallowEqual)
  const [id, setId] = useState(match.params.id)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [shouldDisplayExitIntentPrompt, setShouldDisplayExitIntentPrompt] = useState(false)
  const [menuRef, setMenuRef] = useState(useContext(AdminContext))
  const [notifications, setNotifications] = useState({
    errors: [],
    warnings: [],
    currentIssue: null,
    reorderMode: false,
  })
  const [configNotifications, setConfigNotifications] = useState({
    errors: [],
    warnings: [],
  })

  const { template } = anyChange
  const {
    handleSaveCallback,
    longPoll,
    saveStatus,
    setSaveStatus,
    LogStatus,
    showFailedToSaveError,
  } = useSaveTemplate({ dispatch })

  const hasErrors = notifications.errors.length || configNotifications.errors.length
  const allNotifications = [
    ...configNotifications.errors,
    ...notifications.errors,
    ...notifications.warnings,
  ]

  const { activeStep, isLastStep, templateNavigationConfig, handleNext, handlePrevious } =
    useTemplateNavigation({
      templateId: id,
    })

  const requiresApprovers =
    Boolean(template?.settings?.require_approval) && isEmpty(template?.approvers)
  const saveDisabled = Boolean(hasErrors) || requiresApprovers || saveStatus.saving

  const saveButtonToolTip = () => {
    let title = ''
    if (hasErrors) {
      title =
        'See the notifications button in the top right corner for details as to why this configuration is invalid'
    }
    if (saveStatus.saving) {
      title = 'Save in progress'
    }
    return title
  }

  /*
    Display the exit intent prompt when the user has made changes and is ready to validate
    That prop is set to true when the user has made changes to the template inside the reducer.
  */
  useEffect(() => {
    const readyToValidate = template.uuid && anyChange.readyToValidate

    if (readyToValidate) {
      setShouldDisplayExitIntentPrompt(anyChange.hasUserChangedData)
    } else {
      setShouldDisplayExitIntentPrompt(false)
    }
  }, [template.uuid, anyChange.readyToValidate, anyChange.hasUserChangedData])

  useEffect(() => {
    if (!template?.name) return
    addPrefix(`Edit ${template.name}`)

    return () => {
      removePrefix()
    }
  }, [template.name])

  useEffect(() => {
    if (!Object.values(outboundById).length) {
      fetchIntegrations()
    }
  }, [fetchIntegrations])

  useEffect(() => {
    if (!fieldsetLoading && !listLoading && !patternsLoading) {
      dispatch(readyToValidate())
    }
  }, [fieldsetLoading, listLoading, patternsLoading])

  useEffect(() => {
    if (
      Object.values(outboundById).length &&
      id === 'new' &&
      !template.uuid &&
      !location.search.includes('duplicate')
    ) {
      const email = Object.values(outboundById).find(o => o.slug === 'email-output')
      dispatch(initTemplate(email.id))
    }
  }, [outboundById])

  useEffect(() => {
    if (id === 'new' && template.id) {
      history.replace(location.pathname.replace('new', template.id))
      setId(template.id)
    }
  }, [anyChange])

  useEffect(() => {
    if (anyChange.readyToValidate) {
      const newIssues = {
        ...notifications,
        ...validateTemplate(anyChange, listsById, fieldSetsById, patternsById),
        currentIssue: null,
      }
      if (!isEqual(newIssues, notifications)) {
        setNotifications(newIssues)
      }
    }
  }, [anyChange, patternsById, listsById, fieldSetsById])

  useEffect(() => {
    if (id === 'new') {
      if (location.search.includes('duplicate')) {
        const duplicateId = location.search.split('=')[1]
        api.get(`/api/v1/account/template/${duplicateId}`).then(res => {
          dispatch(setTemplate(duplicateTemplate(res.data)))
        })
      }
    } else {
      api.get(`/api/v1/account/template/${id}`).then(res => {
        dispatch(setTemplate(res.data))
      })
      api
        .get(`/api/v1/account/template/${id}/template-logs`, {
          params: {
            isSaving: true,
          },
        })
        .then(res => {
          setSaveStatus(prevState => ({
            ...prevState,
            saving: !!res.data,
          }))
          if (res.data) {
            longPoll(id)
          } else {
            api.get(`/api/v1/account/template/${id}/template-logs`).then(res => {
              if (res.data.status === LogStatus.FAILED) {
                showFailedToSaveError(
                  res.data,
                  dispatch,
                  'Template integrations failed to validate.',
                )
              }
            })
          }
        })
    }

    fetchPatterns()
    fetchLists()
    fetchAllFieldSetsAndFields()
    return () => {
      dispatch(resetStore())
    }
  }, [])

  useEffect(() => {
    if (id === 'new' && !fieldsetLoading) {
      setSaveStatus(prevState => ({
        ...prevState,
        saving: false,
      }))
    }
  }, [fieldsetLoading])

  if (location === '/admin/templates/edit-template') {
    history.replace('/admin/templates/edit-template/new/setup')
  }

  useEffect(() => {
    setMenuRef({ ...menuRef })
  }, [menuRef.menuRef])

  const handleSave = () => {
    handleSaveCallback(anyChange)
  }

  const displayNotifications = e => {
    if (
      notifications.errors.length ||
      notifications.warnings.length ||
      configNotifications.errors.length
    ) {
      setAnchorEl(e.target)
      setShowNotifications(true)
    }
  }

  const navigateToNotifcation = notif => {
    history.replace(notif.reference.step)
    setShowNotifications(false)
    setNotifications({ ...notifications, currentIssue: notif })

    setTimeout(() => {
      setNotifications({ ...notifications, currentIssue: null })
    }, 1000)
  }

  const TemplateNameWithLink = ({ id, name }) => {
    if (!id) return name

    return (
      <Link
        to={routeByParam({ url: ROUTES.TEMPLATE_DATA, params: { id } })}
        className="underline"
        target="_blank"
      >
        {name}
      </Link>
    )
  }

  return (
    <EditTemplateContext.Provider
      value={{
        notifications,
        setNotifications,
        configNotifications,
        setConfigNotifications,
        requestCache: requestCache.current,
      }}
    >
      <motion.div
        css={css.root}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 0.5 } }}
        exit={{ opacity: 0, transition: { duration: 0.5 } }}
      >
        <AdminHeader nav={templateNavigationConfig}>
          <AdminHeader.PageIcon>
            <HeaderIcon color="hsla(194, 47%, 20%, 1)" size={28} />
          </AdminHeader.PageIcon>
          <div className="ml-4">
            <AdminHeader.Title>
              {t`Templates:`} <TemplateNameWithLink id={template.id} name={template?.name} />
            </AdminHeader.Title>
            <AdminHeader.Subtitle>
              {t`Control exactly what users can see and use during the submission process`}
            </AdminHeader.Subtitle>
          </div>
        </AdminHeader>
        <div css={css.main} id="editTemplateMain" data-test-page-id="edit-template">
          <Header
            hasErrors={Boolean(hasErrors)}
            notifications={notifications}
            setNotifications={setNotifications}
            setShowPreviewModal={setShowPreviewModal}
            displayNotifications={displayNotifications}
            activeStepIndex={activeStep}
            configNotifications={configNotifications}
            readyToValidate={anyChange.readyToValidate}
          />

          {loading ? (
            <div css={css.circularProgress}>
              <CircularProgress />
            </div>
          ) : (
            <Switch>
              <Route default path="/admin/templates/edit-template/:id/setup" component={Setup} />
              <Route path="/admin/templates/edit-template/:id/govern" component={Schema} />
              <Route path="/admin/templates/edit-template/:id/connect" component={Integrate} />
            </Switch>
          )}
        </div>
        <div css={css.footer}>
          <Grid container spacing={3}>
            <Grid item xs={4} />
            <Grid item xs={4} />
            <Grid item xs={4} sx={css.navButtonContainer}>
              <Button
                onClick={handlePrevious}
                className={classes.button}
                disabled={!activeStep}
                data-testid="previous-button"
              >
                {t`Previous`}
              </Button>
              <Button
                onClick={handleNext}
                className={classes.button}
                disabled={isLastStep}
                data-testid="next-button"
              >
                {t`Next`}
              </Button>
              <Tooltip title={saveButtonToolTip()} placement="top" data-testid="tooltip">
                <span>
                  <CvsButton
                    name="save-template"
                    color="primary"
                    disabled={saveDisabled}
                    onClick={handleSave}
                    data-testid="save-button"
                  >
                    {saveStatus.saving && !fieldsetLoading ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.5rem' }}>
                        <CircularProgress size={18} />
                        <div>{t`Saving`}</div>
                      </span>
                    ) : (
                      <div>{t`Save`}</div>
                    )}
                  </CvsButton>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        </div>
        <Dialog
          open={showPreviewModal}
          fullWidth={true}
          maxWidth={false}
          onClose={() => setShowPreviewModal(false)}
          aria-labelledby="max-width-dialog-title"
        >
          <Preview template={anyChange} onClose={() => setShowPreviewModal(false)} />
        </Dialog>
        {showNotifications ? (
          <ClickAwayListener onClickAway={() => setShowNotifications(false)}>
            <Popper
              open={showNotifications}
              onClose={() => setShowNotifications(false)}
              anchorEl={anchorEl}
              sx={css.popper}
            >
              <Paper elevation={5} sx={css.notifications}>
                <div css={css.notificationContainer}>
                  {allNotifications.map((notif, i) => (
                    <div
                      key={`notif-${i}`}
                      onClick={() => navigateToNotifcation(notif)}
                      css={css.notification}
                    >
                      {notif.type === 'ERROR' ? (
                        <Alert severity="error">
                          <AlertTitle>{t`Error`}</AlertTitle>
                          {notif.message}
                        </Alert>
                      ) : (
                        <Alert severity="warning">
                          <AlertTitle>{t`Warning`}</AlertTitle>
                          {notif.message}
                        </Alert>
                      )}
                    </div>
                  ))}
                </div>
              </Paper>
            </Popper>
          </ClickAwayListener>
        ) : null}
      </motion.div>
      <Prompt
        when={shouldDisplayExitIntentPrompt}
        message={location => {
          // return true when the location is still on the template edit. This prevents
          // the prompt from displaying when moving forward to next step
          if (location.pathname.includes('/admin/templates/edit-template/')) return true

          return t`You are about to lose any unsaved changes by leaving this page. Are you sure you want to continue?`
        }}
      />
    </EditTemplateContext.Provider>
  )
}
